import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

let purchasePending = false;
const stateEvent = 'kunnie:brand-offer-state';

/** One explicit purchase of exactly the displayed variants; successful adds open the existing cart drawer. */
class KunnieBrandOffer extends Component {
  #controller;
  #copy;
  #key;
  #busy = false;
  #state;

  connectedCallback() { super.connectedCallback(); this.#connect(); }
  updatedCallback() { super.updatedCallback(); this.#connect(); }
  disconnectedCallback() { super.disconnectedCallback(); this.#controller?.abort(); }

  #snapshot(root = this) {
    const form = root.querySelector('[data-offer-form]');
    return {
      currency: root.dataset.currency,
      total: Number(root.querySelector('[data-offer-total]')?.dataset.offerTotal),
      ids: [...(form?.querySelectorAll('input[name$="[id]"]') || [])].map(input => input.value),
      components: [...root.querySelectorAll('[data-offer-component]')].map(item => ({ id: item.dataset.offerComponent, price: Number(item.dataset.price), available: item.dataset.available === 'true' })),
      available: !form?.querySelector('[data-offer-add]')?.disabled,
    };
  }

  #connect() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const copy = this.querySelector('[data-offer-copy]');
    if (!copy) return;
    this.#copy = JSON.parse(copy.textContent);
    const snapshot = this.#snapshot();
    this.#key = 'kunnie:brand-offer:' + snapshot.currency + ':' + snapshot.ids.join(',');
    const button = this.querySelector('[data-offer-add]');
    button.dataset.unavailable = String(!snapshot.available);
    button.dataset.label = button.querySelector('[data-offer-button-label]').textContent;
    this.querySelector('form')?.addEventListener('submit', this.#add, { signal });
    document.addEventListener(stateEvent, () => this.#paint(), { signal });
    document.addEventListener('shopify:cart:lines-update', async (event) => {
      try { const result = await event.promise; await this.#reconcile(result?.detail?.items); } catch { /* Existing cart feedback owns its errors. */ }
    }, { signal });
    this.#paint();
    this.#reconcile();
  }

  async #reconcile(items) {
    if (!this.#state?.confirmed || this.#busy) return;
    try {
      if (!Array.isArray(items)) {
        const response = await fetch(Theme.routes.cart_url + '.js', { credentials: 'same-origin', cache: 'no-store', signal: this.#controller.signal });
        if (!response.ok) return;
        items = (await response.json()).items;
      }
      if (!items.some(line => line.properties?._kunnie_brand_group === this.#state?.transaction)) {
        sessionStorage.removeItem(this.#key);
        this.#status('');
        document.dispatchEvent(new Event(stateEvent));
      }
    } catch { /* Keep a confirmed purchase guarded when cart state cannot be read. */ }
  }

  #paint() {
    try { this.#state = JSON.parse(sessionStorage.getItem(this.#key) || 'null'); } catch { this.#state = null; }
    const button = this.querySelector('[data-offer-add]');
    if (!button) return;
    button.disabled = purchasePending || !!this.#state || button.dataset.unavailable === 'true';
    button.setAttribute('aria-busy', String(this.#busy));
    button.querySelector('[data-offer-button-label]').textContent = this.#busy ? this.#copy.adding : this.#state?.confirmed ? this.#copy.added : button.dataset.label;
    this.querySelector('[data-offer-cart]').hidden = !this.#state;
    if (this.#state && !this.#busy) this.#status(this.#state.confirmed ? this.#copy.added : this.#copy.error);
  }

  #status(message) { this.querySelector('[data-offer-status]').textContent = message; }

  #add = async (event) => {
    event.preventDefault();
    if (purchasePending || this.#state || this.querySelector('[data-offer-add]').disabled) return;
    purchasePending = true;
    this.#busy = true;
    this.#status('');
    document.dispatchEvent(new Event(stateEvent));
    let submitted = false;
    try {
      const chosen = this.#snapshot();
      const url = new URL(location.href);
      url.searchParams.set('section_id', this.dataset.section);
      const refreshed = await fetch(url, { cache: 'no-store', signal: this.#controller.signal });
      if (!refreshed.ok) throw new Error('catalog');
      const fresh = [...new DOMParser().parseFromString(await refreshed.text(), 'text/html').querySelectorAll('kunnie-brand-offer')].find(item => item.dataset.offerId === this.dataset.offerId);
      if (!fresh) throw new Error('catalog');
      const current = this.#snapshot(fresh);
      // The busy button is disabled only locally; the fresh server markup owns availability.
      const displayed = { ...chosen, available: true };
      if (JSON.stringify(current) !== JSON.stringify(displayed) || !current.components.every(item => item.available)) {
        this.replaceChildren(...fresh.childNodes);
        this.#connect();
        this.#status(this.#copy.changed);
        return;
      }
      const readCart = async () => {
        const response = await fetch(Theme.routes.cart_url + '.js', { credentials: 'same-origin', cache: 'no-store', signal: this.#controller.signal });
        if (!response.ok) throw new Error('cart');
        return response.json();
      };
      const transaction = crypto.randomUUID();
      const items = chosen.ids.map(id => ({ id, quantity: 1, properties: { _kunnie_brand_group: transaction, _kunnie_brand_offer: this.dataset.offerId } }));
      const sections = [...new Set([...(this.dataset.bundle === 'true' ? ['kunnie-cart-bundle-proof'] : []), ...[...document.querySelectorAll('cart-items-component')].map(item => item.dataset.sectionId).filter(Boolean)])].slice(0, 5);
      // Persist before the POST. An ambiguous response must not silently submit the selection twice.
      sessionStorage.setItem(this.#key, JSON.stringify({ transaction, confirmed: false }));
      submitted = true;
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: location.pathname + '?_kunnie_offer=' + transaction }), signal: this.#controller.signal,
      });
      const data = await response.json();
      const cart = await readCart();
      const readProof = (html) => {
        const element = new DOMParser().parseFromString(html || '', 'text/html').querySelector('[data-kunnie-cart-bundles]');
        return element ? JSON.parse(element.textContent) : null;
      };
      let proof = readProof(data.sections?.['kunnie-cart-bundle-proof']);
      if (response.ok && !data.status && this.dataset.bundle === 'true' && !proof?.items.some(parent => parent.brand_group === transaction)) {
        // A bundled section response can lag behind the Ajax cart. Read this transaction without caching.
        const proofUrl = new URL(location.href);
        proofUrl.searchParams.set('sections', sections.join(','));
        proofUrl.searchParams.set('_kunnie_readback', transaction);
        const response = await fetch(proofUrl, { cache: 'no-store', signal: this.#controller.signal });
        if (!response.ok) throw new Error('proof');
        const refreshedSections = await response.json();
        proof = readProof(refreshedSections['kunnie-cart-bundle-proof']);
        data.sections = { ...data.sections, ...refreshedSections };
      }
      const confirmed = response.ok && !data.status && cart.currency === chosen.currency && items.every(item => cart.items.some(line => {
        if (String(line.variant_id) !== item.id || line.quantity !== 1 || line.properties?._kunnie_brand_group !== transaction) return false;
        if (this.dataset.bundle !== 'true') return true;
        const components = proof?.items.find(parent => String(parent.variant_id) === item.id && parent.brand_group === transaction)?.components || [];
        return line.final_line_price === chosen.total && components.length === chosen.components.length && chosen.components.every(item => components.some(part => String(part.variant_id) === item.id && part.quantity === 1));
      }));
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({ action: confirmed ? 'add' : 'update', context: 'product', lines: items.map(item => ({ merchandiseId: item.id, quantity: 1 })), promise: deferred.promise }));
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, sections: data.sections, source: 'kunnie-brand-offer', didError: !confirmed } });
      if (!confirmed) throw new Error('unconfirmed');
      sessionStorage.setItem(this.#key, JSON.stringify({ transaction, confirmed: true }));
      this.#status(this.#copy.added);
    } catch (error) {
      if (error.name !== 'AbortError') this.#status(submitted ? this.#copy.error : this.#copy.unavailable);
    } finally {
      purchasePending = false;
      this.#busy = false;
      document.dispatchEvent(new Event(stateEvent));
    }
  };
}

if (!customElements.get('kunnie-brand-offer')) customElements.define('kunnie-brand-offer', KunnieBrandOffer);
